import type { ZodError, ZodIssue } from "zod";

enum ErrorType {
  Public = "PublicError",
  Internal = "InternalError",
}
class PublicError extends Error {
  public readonly type = ErrorType.Public;
  constructor(message: string) {
    super(message);
    this.name = ErrorType.Public;
  }
}

class InternalError extends Error {
  public readonly type = ErrorType.Internal;
  constructor(message: string) {
    super(message);
    this.name = ErrorType.Internal;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isPublicError(error: any): error is PublicError {
  return error?.type === ErrorType.Public || error?.name === ErrorType.Public;
}

class AppError extends Error {
  readonly _tag: string = "AppError";

  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

/**
 * A generic factory to create tagged error classes.
 * @param tag - The literal string type of the tag (e.g., "ValidationError").
 */
function TaggedError<TTag extends string, TCause extends Error = Error>(
  tag: TTag
) {
  class ITaggedError extends AppError {
    override readonly _tag: TTag;
    readonly cause?: TCause;

    constructor(args?: { message?: string; cause?: TCause }) {
      super(args?.message ?? tag);
      this.name = tag;
      this._tag = tag;
      if (args?.cause) {
        this.cause = args.cause;
      }
    }
  }
  return ITaggedError;
}

class ValidationError extends TaggedError<"ValidationError", ZodError>(
  "ValidationError"
) {
  public readonly issues: ZodIssue[];
  constructor(cause: ZodError, props?: { message?: string }) {
    super({ message: props?.message ?? "Validation failed", cause });
    this.issues = cause.issues;
  }
}

class TaggedPublicError<C extends AppError> extends TaggedError(
  "TaggedPublicError"
) {
  override readonly cause: C;
  readonly causeTag: C["_tag"];
  constructor(message: string, cause: C) {
    super({ message });
    this.cause = cause;
    this.causeTag = cause._tag;
  }
}

function isTaggedPublicError(
  error: Error
): error is TaggedPublicError<AppError> {
  return (
    error instanceof TaggedPublicError &&
    error._tag === "TaggedPublicError" &&
    typeof error.causeTag === "string" &&
    error.cause instanceof AppError
  );
}

export {
  // V1 compatibility
  InternalError,
  isPublicError,
  PublicError,

  // New error types
  AppError,
  TaggedError,
  ValidationError,
  TaggedPublicError,
  isTaggedPublicError,
};
